import React, { memo } from 'react';
import { GridSize, makeStyles } from '@material-ui/core';
import { BodyS } from 'components/Typography/Typography';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import Accordion from 'components/Accordion/Accordion';
import { translate } from 'components/Translation';
import styles from 'assets/jss/material-dashboard-pro-react/components/customFilterStyle';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import CustomTag from 'components/CustomTag/CustomTag';
import AccordionElement from 'components/CustomFilter/AccordionElement';

// @ts-ignore
const useStyles = makeStyles(styles);

export type FilterType = 'multiple' | 'one';

interface IElementItem {
	title: string;
	value: string;
	show?: React.ReactElement;
	color?: string;
	tagText?: string;
	label?: string;
}

export interface IFilter {
	type: FilterType;
	title: string;
	name: string;
	elements: IElementItem[];
	disableOnBlur?: boolean;
	label?: string;
}

export interface FilterValue {
	[key: string]: string[];
}

interface IFilterProps {
	filters: IFilter[];
	onChange: (e: string, val: any[]) => void;
	value: FilterValue;
	xs?: GridSize;
	sm?: GridSize;
	lg?: GridSize;
	label?: string;
}

const Filters = ({ filters, onChange, value, xs, sm, lg, label }: IFilterProps) => {
	const classes = useStyles();
	const hasAnyMultipleFiltersSelected =
		filters.some((filter) => filter.type === 'multiple') &&
		Object.values(value).some((filter) => Array.isArray(filter) && filter.length);
	const removeTag = (filterName: string, tag: string) =>
		value[filterName].filter((arrValue: string) => arrValue !== tag);
	const clearAllTags = () => Object.keys(value).forEach((key) => onChange(key, []));

	const getTag = (key: string, tag: string): IElementItem | null | undefined => {
		const selectedTag = filters.find((filter) => filter.name === key);
		if (selectedTag) {
			return selectedTag.elements.find((element) => {
				return element.value === tag;
			});
		}
		return null;
	};

	const getTagsArray = () => {
		return Object.keys(value).map((filterName: string) => {
			const currentTagsArray = Object.keys(value).length && value[filterName];

			return (
				Array.isArray(currentTagsArray) &&
				currentTagsArray.map((tag: string): React.ReactElement | undefined => {
					const currentTag = getTag(filterName, tag);
					if (!currentTag) {
						return undefined;
					}
					return (
						<CustomTag
							key={tag}
							onClick={() => onChange(filterName, removeTag(filterName, tag))}
							label={currentTag?.tagText || currentTag?.title}
							color={currentTag?.color}
						/>
					);
				})
			);
		});
	};

	const displayTags = () => (
		<ConditionalRender show={hasAnyMultipleFiltersSelected}>
			<CustomTag
				clearAll
				key={'clear-all'}
				onClick={() => {
					clearAllTags();
				}}
				label={translate('clear-all')}
			/>
			{/* TODO: add proper typing */}
			{/* @ts-ignore */}
			{getTagsArray()}
		</ConditionalRender>
	);
	return (
		<>
			<GridContainer justify="flex-start">
				{filters.map((filter) => (
					<>
						<GridItem
							key={filter.name}
							className={`filter-${filter.name}`}
							xs={xs ? xs : 12}
							sm={sm ? sm : 6}
							lg={lg ? lg : 3}
						>
							<BodyS>{filter.label}</BodyS>

							<Accordion
								className={classes.accordionTitleContainer}
								collapses={[
									{
										title: (
											<div className={classes.accordionTitle}>
												<BodyS>{filter.title}</BodyS>
											</div>
										),
										content: (
											<AccordionElement
												elements={filter.elements}
												onChange={(value) => onChange && onChange(filter.name, value)}
												checked={!!value && value[filter.name]}
												type={filter.type}
											/>
										),
									},
								]}
								enableOnBlur={!filter.disableOnBlur}
							/>
						</GridItem>
					</>
				))}
			</GridContainer>
			<div className={classes.filtersWrapper}>{displayTags()}</div>
		</>
	);
};

export default memo(Filters);
